import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { loadTariffe, provinciaToZona, provinciaToRegione } from '../lib/dataloader'
import { calcolaPreventivo, calcolaPlaceholder, compagniaApplicabile, AUTOCARRI_INTERMEDIARI_CODICE } from '../lib/calcoli'
import { categorieCompagnieAmmesse, richiedePolizza, mappaTipoOperazioneLegacy, durataFissa, tipiOperazioneAmmessi, inferiCausaleDaLegacy, causaleToStatoVeicolo, validaCausaleNuova, validaCausaleRinnovo, compagnieAmmessePerScenario, isAutocarriIntermediari, valoreVeicoloValido } from '../lib/validazioni'

import StepTipoOperazione from '../components/wizard/StepTipoOperazione'
import StepDatiComuni from '../components/wizard/StepDatiComuni'
import StepStandalone from '../components/wizard/StepStandalone'
import StepComparazione from '../components/wizard/StepComparazione'
import StepDocumenti from '../components/wizard/StepDocumenti'
import StepInvio from '../components/wizard/StepInvio'

// Step variabili in base al tipo_operazione.
// Flusso polizza (concessionari/annuali): cliente+veicolo → standalone → comparazione
// Flusso solo_standalone: solo cliente → standalone (no comparazione)
function buildSteps(tipoOp) {
  const steps = [
    { id: 'tipo',       label: 'Tipo operazione' },
    { id: 'comuni',     label: tipoOp === 'solo_standalone' ? 'Cliente' : 'Dati comuni' },
    { id: 'standalone', label: 'Stand alone' },
  ]
  if (richiedePolizza(tipoOp)) {
    steps.push({ id: 'comparazione', label: 'Comparazione' })
  }
  steps.push({ id: 'documenti', label: 'Documenti' })
  steps.push({ id: 'invio',     label: 'Invio' })
  return steps
}

const INITIAL = {
  tipo_operazione: '',  // concessionari | annuali | solo_standalone
  cliente_nome: '',
  cliente_cognome: '',
  dati_comuni: {
    valore_auto: '',
    immatricolazione: '',
    provincia: '',
    durata_mesi: 12,
    causale: '',           // 'nuova' | 'usata' | 'rinnovo'
    stato_veicolo: '',     // derivato da causale (nuova → 'nuovo', usata/rinnovo → 'usato')
    categoria_veicolo: 'auto', // 'auto' | 'autocarro' (max 35q)
    peso_autocarro: '',
    is_societa: false,     // se true, richiede visura camerale tra i documenti
    satellitare: false,
    sat_marca: '',
    sat_seriale: '',
    acquista_lojack: false,
    mesi_lojack: null,
    mesi_valore_a_nuovo: null,
    // VAN dedicato Quixa/Sogessur (tariffe SENZA valore a nuovo): di norma
    // vuoto = nessun VAN; valorizzato solo se l'operatore lo richiede.
    mesi_valore_a_nuovo_qs: null,
    pacchetto: 'BASE',
    pacchetto_autocarro: '',
    cristalli_massimale: null,
    // Dati aggiuntivi raccolti allo step Documenti (necessari per stipula polizza)
    colore_auto: '',
    km: null,
    cellulare_contraente: '',
    codice_fiscale: '',
    indirizzo_residenza: '',
    cap_residenza: '',
    citta_residenza: '',
    provincia_residenza: '',
    numero_telaio: '',
    email_contraente: '',
    richiesta_preventivo_rca: false,
    // Identificativi veicolo (obbligatori per tutti i tipi inclusi solo_standalone)
    targa: '',
    marca_veicolo: '',
    modello_veicolo: '',
  },
  compagnie_selezionate: [],
  preventivi: {},
  standalone_selezionati: [],   // [{codice, anni}]
  scelta_compagnia: null,
  totale_finale: null,
  // Ricarico scelto dal dealer/subagente per questa pratica.
  // Range 0..50%, default 0. Si applica nel comparatore al premio finale
  // visibile (polizza + satellitare/GAP + stand-alone), non dentro i motori tariffari.
  commissione_pct_pratica: 0,
  stato: 'bozza',
}

// Tetto fisso per il ricarico applicabile in comparazione
export const COMMISSIONE_MAX_PCT = 50

// Quando si riprende una bozza, sceglie il primo step ancora "incompleto"
// in base al tipo operazione e ai campi già compilati.
function stepInizialeBozza(row) {
  const steps = buildSteps(row.tipo_operazione)
  const idxOf = (id) => steps.findIndex(s => s.id === id)

  if (!row.tipo_operazione) return 0

  if (row.tipo_operazione === 'solo_standalone') {
    // Flusso ridotto: cliente → standalone → documenti → invio
    if (!row.cliente_nome || !row.cliente_cognome) return idxOf('comuni')
    if (!row.standalone_selezionati?.length) return idxOf('standalone')
    // SEMPRE riparti da documenti: i file caricati non vengono persistiti
    // tra le sessioni (vivono solo nello state React), quindi vanno ricaricati.
    return idxOf('documenti')
  }

  // Flusso polizza
  const dc = row.dati_comuni || {}
  if (!row.cliente_nome || !dc.provincia || !dc.valore_auto) return idxOf('comuni')
  if (!row.scelta_compagnia) return idxOf('comparazione')
  // SEMPRE riparti da documenti se la bozza è arrivata fin lì o oltre, perché
  // gli allegati non sono salvati in DB e l'admin deve riceverli per email.
  return idxOf('documenti')
}

export default function NuovaPratica() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const { id: praticaIdFromUrl } = useParams()  // se presente, è una bozza da riprendere

  const [stepIdx, setStepIdx] = useState(0)
  const [data, setData] = useState(INITIAL)
  const [tariffe, setTariffe] = useState(null)
  const [praticaId, setPraticaId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [loadingBozza, setLoadingBozza] = useState(!!praticaIdFromUrl)
  const commissioneAccountPct = Math.min(50, Math.max(0, parseFloat(profile?.commissione_pct) || 0))

  useEffect(() => {
    loadTariffe().then(setTariffe).catch(e => setError(e.message))
  }, [])

  // Se l'URL contiene un id, ricarica la bozza e popola lo stato
  useEffect(() => {
    if (!praticaIdFromUrl) return
    let cancelled = false
    ;(async () => {
      console.log('[bozza] Caricamento pratica id =', praticaIdFromUrl)
      const { data: row, error: err } = await supabase
        .from('pratiche').select('*').eq('id', praticaIdFromUrl).maybeSingle()

      if (cancelled) return

      if (err) {
        console.error('[bozza] Errore fetch:', err)
        setError(`Errore caricamento bozza: ${err.message}. Codice: ${err.code || '—'}`)
        setLoadingBozza(false)
        return
      }
      if (!row) {
        console.warn('[bozza] Riga non trovata per id', praticaIdFromUrl)
        setError('Pratica non trovata. Potrebbe essere stata eliminata o appartenere a un altro utente.')
        setLoadingBozza(false)
        return
      }
      console.log('[bozza] Riga caricata:', row)

      if (row.stato === 'in_processamento') {
        setError('Questa pratica è già stata inviata: non può più essere modificata.')
        setLoadingBozza(false)
        return
      }

      // Override completo dello state con i dati della bozza.
      // NB: compagnie_selezionate e preventivi verranno comunque rigenerati
      // dagli useEffect di auto-popolazione/ricalcolo in base ai dati attuali.
      const tipoMappato = mappaTipoOperazioneLegacy(row.tipo_operazione) || ''
      const ammessiPerRuolo = tipiOperazioneAmmessi(profile?.ruolo).map(t => t.value)
      const tipoFinale = ammessiPerRuolo.includes(tipoMappato) ? tipoMappato : ''

      // Causale: prendi quella già salvata; se mancante (bozza pre-refactor) inferiscila
      // dal vecchio tipo_operazione legacy (rinnovo/nuova_emissione/voltura).
      const dcRow = row.dati_comuni || {}
      let causaleFinale = dcRow.causale || inferiCausaleDaLegacy(row.tipo_operazione) || ''
      const statoVeicoloFinale = dcRow.stato_veicolo
        || causaleToStatoVeicolo(causaleFinale)
        || ''

      const merged = {
        tipo_operazione: tipoFinale,
        cliente_nome: row.cliente_nome || '',
        cliente_cognome: row.cliente_cognome || '',
        dati_comuni: {
          ...INITIAL.dati_comuni,
          ...dcRow,
          causale: causaleFinale,
          stato_veicolo: statoVeicoloFinale,
          categoria_veicolo: dcRow.categoria_veicolo || 'auto',  // default per bozze pre-refactor
          is_societa: !!dcRow.is_societa,  // default false per bozze pre-refactor
        },
        compagnie_selezionate: row.compagnie_selezionate || [],
        preventivi: row.preventivi || {},
        standalone_selezionati: row.standalone_selezionati || [],
        scelta_compagnia: row.scelta_compagnia || null,
        totale_finale: row.totale_finale ?? null,
        commissione_pct_pratica: row.commissione_pct_pratica ?? 0,
        stato: row.stato || 'bozza',
        created_at: row.created_at || null,  // data immissione preventivo (per decorrenza)
      }
      setData(merged)
      setPraticaId(row.id)
      // Riparte allo step più sensato in base ai dati presenti
      const startStep = stepInizialeBozza(row)
      console.log('[bozza] Step iniziale:', startStep)
      setStepIdx(startStep)
      setLoadingBozza(false)
    })()

    return () => { cancelled = true }
  }, [praticaIdFromUrl])

  const update = (patch) => setData(d => ({ ...d, ...patch }))
  const updateComuni = (patch) => setData(d => ({ ...d, dati_comuni: { ...d.dati_comuni, ...patch } }))

  useEffect(() => {
    if (!isAutocarriIntermediari(data.tipo_operazione)) return
    const patch = {}
    if (data.dati_comuni.categoria_veicolo !== 'autocarro') patch.categoria_veicolo = 'autocarro'
    if (parseInt(data.dati_comuni.durata_mesi, 10) !== 12) patch.durata_mesi = 12
    if (data.dati_comuni.satellitare) patch.satellitare = false
    if (data.dati_comuni.sat_marca) patch.sat_marca = ''
    if (data.dati_comuni.sat_seriale) patch.sat_seriale = ''
    if (data.dati_comuni.acquista_lojack) patch.acquista_lojack = false
    if (data.dati_comuni.mesi_lojack) patch.mesi_lojack = null
    if (Object.keys(patch).length > 0) {
      setData(d => ({ ...d, dati_comuni: { ...d.dati_comuni, ...patch } }))
    }
  }, [data.tipo_operazione, data.dati_comuni])

  // Auto-popolamento delle compagnie applicabili: avviene automaticamente
  // quando cambiano (tipo_operazione, provincia, ruolo). L'utente non deve più
  // selezionare a mano: tutte le compagnie applicabili appaiono in comparazione.
  useEffect(() => {
    if (!tariffe) return
    if (isAutocarriIntermediari(data.tipo_operazione)) {
      if (
        data.compagnie_selezionate.length !== 1
        || data.compagnie_selezionate[0] !== AUTOCARRI_INTERMEDIARI_CODICE
      ) {
        setData(d => ({ ...d, compagnie_selezionate: [AUTOCARRI_INTERMEDIARI_CODICE] }))
      }
      return
    }
    if (!richiedePolizza(data.tipo_operazione)) {
      // Per solo_standalone azzeriamo le compagnie selezionate
      if (data.compagnie_selezionate.length > 0) {
        setData(d => ({ ...d, compagnie_selezionate: [], preventivi: {} }))
      }
      return
    }
    if (!data.dati_comuni.provincia) return

    const regione = provinciaToRegione(data.dati_comuni.provincia, tariffe.province)
    const cats = categorieCompagnieAmmesse(profile?.ruolo, data.tipo_operazione)
    // Filtro stretto per scenario (ruolo × macro × causale). Se ritorna null
    // ricadiamo sul filtro per categoria (retro-compatibilità legacy).
    const codiciScenario = compagnieAmmessePerScenario(
      profile?.ruolo,
      data.tipo_operazione,
      data.dati_comuni.causale
    )
    const applicabili = tariffe.compagnie
      .filter(c => c.attiva)
      .filter(c => codiciScenario ? codiciScenario.includes(c.codice) : cats.includes(c.categoria))
      // Il filtro regionale (compagniaApplicabile) NON viene applicato qui: vogliamo
      // che le compagnie scenario-applicabili compaiano comunque nel comparatore con
      // un messaggio chiaro "Provincia non ammissibile" invece di sparire silenziosamente.
      // Niente filtro su durata: alcune compagnie marcate 'annuale' (es. Lloyd's Plus)
      // hanno tariffe poliennali. Se non ci sono tariffe per la durata richiesta
      // il preventivo verrà semplicemente N/D, ma la compagnia resta visibile.
      .map(c => c.codice)

    // Aggiorna solo se diverso (evita loop infiniti di setState)
    const same = applicabili.length === data.compagnie_selezionate.length
      && applicabili.every(c => data.compagnie_selezionate.includes(c))
    if (!same) setData(d => ({ ...d, compagnie_selezionate: applicabili }))
  }, [tariffe, data.tipo_operazione, data.dati_comuni.provincia, data.dati_comuni.durata_mesi, data.dati_comuni.causale, profile?.ruolo])

  // Forza durata_mesi=12 per:
  //  - macro "annuali" (sempre 12)
  //  - causale "rinnovo" (un rinnovo è sempre annuale, anche su concessionari)
  useEffect(() => {
    const fissa = durataFissa(data.tipo_operazione, data.dati_comuni.causale)
    if (fissa && data.dati_comuni.durata_mesi !== fissa) {
      setData(d => ({ ...d, dati_comuni: { ...d.dati_comuni, durata_mesi: fissa } }))
    }
  }, [data.tipo_operazione, data.dati_comuni.causale])

  // Per il flusso solo_standalone: totale = somma stand-alone × anni
  useEffect(() => {
    if (data.tipo_operazione !== 'solo_standalone') return
    if (!tariffe) return
    const totale = (data.standalone_selezionati || []).reduce((acc, sel) => {
      const prod = tariffe.standalone.find(p => p.codice === sel.codice)
      if (!prod) return acc
      return acc + parseFloat(prod.prezzo_annuo || 0) * (sel.anni || 1)
    }, 0)
    const arrotondato = Math.round(totale * 100) / 100
    if (data.totale_finale !== arrotondato || data.scelta_compagnia !== null) {
      setData(d => ({ ...d, totale_finale: arrotondato, scelta_compagnia: null }))
    }
  }, [tariffe, data.tipo_operazione, data.standalone_selezionati])

  // Recalc preventivi al variare di dati / compagnie / commissione
  useEffect(() => {
    if (!tariffe) return
    if (!richiedePolizza(data.tipo_operazione)) return
    if (!data.dati_comuni.provincia || !data.dati_comuni.valore_auto) return
    const prev = {}
    const sigla = data.dati_comuni.provincia
    const valore_auto = parseFloat(data.dati_comuni.valore_auto)
    const datiBase = { ...data.dati_comuni, valore_auto }
    const regioneCalc = provinciaToRegione(sigla, tariffe.province)
    for (const codice of data.compagnie_selezionate) {
      if (codice === AUTOCARRI_INTERMEDIARI_CODICE) {
        prev[codice] = calcolaPreventivo(codice, {
          dati: datiBase,
          paramComp: tariffe.parametriCompagnia[AUTOCARRI_INTERMEDIARI_CODICE] || {},
          commissioneDealerPct: commissioneAccountPct,
        })
        continue
      }
      const comp = tariffe.compagnie.find(c => c.codice === codice)
      if (!comp) continue
      if (comp.placeholder) {
        prev[codice] = calcolaPlaceholder(codice, comp.nome)
        continue
      }
      if (codice === 'EIR' && parseInt(datiBase.durata_mesi, 10) !== 12) {
        prev[codice] = {
          ok: false,
          compagnia: codice,
          errors: ['La tariffa EIR è disponibile solo per 12 mesi'],
        }
        continue
      }
      // Verifica provincia ammissibile prima di chiamare il calc.
      // Se non ammissibile, restituiamo subito un placeholder con messaggio pulito,
      // così la card appare nel comparatore con "Provincia non ammissibile" invece
      // di un errore tecnico verboso del calc.
      if (!compagniaApplicabile(comp, regioneCalc)) {
        prev[codice] = {
          ok: false,
          compagnia: codice,
          reason_code: 'PROVINCE_NOT_ALLOWED',
          errors: ['Provincia non ammissibile per questa compagnia'],
        }
        continue
      }
      // Arricchisce i dati con le zone specifiche per compagnia
      const dati = {
        ...datiBase,
        zona: provinciaToZona(sigla, tariffe.EIR.zoneProvince),       // EIR
        zona_lloyd_plus: tariffe.LLOYD_PLUS.zoneProvince[sigla] || null,
        zona_covea: tariffe.COVEA.zoneProvince[sigla] || null,
        zona_quixa: tariffe.QUIXA.zoneProvince[sigla] || null,
        zona_sogessur: tariffe.SOGESSUR.zoneProvince[sigla] || null,
      }
      const args = {
        dati,
        // EIR
        tassi: codice === 'EIR' ? tariffe.EIR.tassi
              : codice === 'LLOYD_VOLANTE' ? tariffe.LLOYD_VOLANTE.tassi
              : codice === 'LLOYD_PLUS' ? tariffe.LLOYD_PLUS.tassi
              : codice === 'COVEA' ? tariffe.COVEA.tassi
              : codice === 'QUIXA' ? tariffe.QUIXA.tassi
              : codice === 'SOGESSUR' ? tariffe.SOGESSUR.tassi
              // Legacy: COVEA annuale ora unificato
              : (codice === 'COVEA_ANNUAL' || codice === 'COVEA_ANNUALE') ? tariffe.COVEA.tassi
              : [],
        parametri: tariffe.EIR.parametri,
        coeffDurata: tariffe.EIR.coeffDurata,
        paramComp: tariffe.parametriCompagnia[codice] || {},
        parametriGlobali: tariffe.parametriGlobali,
        // Competenza fissa dell'account. Lo slider pratica viene applicato dopo,
        // in comparazione, sul premio gia' comprensivo di questa competenza.
        commissioneDealerPct: commissioneAccountPct,
      }
      prev[codice] = calcolaPreventivo(codice, args)
    }
    setData(d => {
      const scelta = d.scelta_compagnia
      if (!scelta || !prev[scelta]?.ok) return { ...d, preventivi: prev }

      const standaloneTotal = (d.standalone_selezionati || []).reduce((acc, sel) => {
        const prod = tariffe.standalone.find(p => p.codice === sel.codice)
        if (!prod) return acc
        return acc + parseFloat(prod.prezzo_annuo || 0) * (sel.anni || 1)
      }, 0)
      const baseFinale = (parseFloat(prev[scelta].totale) || 0) + standaloneTotal
      const surplus = parseFloat(d.commissione_pct_pratica) || 0
      const totaleFinale = Math.round(baseFinale * (1 + surplus / 100) * 100) / 100

      return { ...d, preventivi: prev, totale_finale: totaleFinale }
    })
  }, [tariffe, data.tipo_operazione, data.dati_comuni, data.compagnie_selezionate, profile?.commissione_pct])

  async function persist(extra = {}) {
    if (!profile) return
    setSaving(true)
    const payload = {
      dealer_id: profile.id,
      tipo_operazione: data.tipo_operazione,
      cliente_nome: data.cliente_nome,
      cliente_cognome: data.cliente_cognome,
      dati_comuni: data.dati_comuni,
      compagnie_selezionate: data.compagnie_selezionate,
      preventivi: data.preventivi,
      standalone_selezionati: data.standalone_selezionati,
      scelta_compagnia: data.scelta_compagnia,
      totale_finale: data.totale_finale,
      commissione_pct_pratica: data.commissione_pct_pratica,
      stato: data.stato,
      ...extra,
    }
    let res
    if (praticaId) {
      res = await supabase.from('pratiche').update(payload).eq('id', praticaId).select().single()
    } else {
      res = await supabase.from('pratiche').insert(payload).select().single()
      if (res.data) setPraticaId(res.data.id)
    }
    setSaving(false)
    if (res.error) { setError(res.error.message); return null }
    // Memorizza la data di immissione (created_at) dal DB la prima volta, così
    // la decorrenza è coerente con il valore autorevole del database.
    if (res.data?.created_at) {
      setData(d => (d.created_at ? d : { ...d, created_at: res.data.created_at }))
    }
    return res.data
  }

  // STEPS dinamici in base al tipo operazione
  const STEPS = buildSteps(data.tipo_operazione)

  function next() { setStepIdx(i => Math.min(i + 1, STEPS.length - 1)) }
  function prev() { setStepIdx(i => Math.max(i - 1, 0)) }

  if (error) return (
    <div>
      <div className="alert alert-error">Errore: {error}</div>
      <button className="btn" onClick={() => navigate('/pratiche')}>← Torna alle pratiche</button>
    </div>
  )
  if (!tariffe || loadingBozza) return <div className="loading">Caricamento{loadingBozza ? ' bozza' : ' tariffe'}...</div>

  const regioneCliente = data.dati_comuni.provincia
    ? provinciaToRegione(data.dati_comuni.provincia, tariffe.province)
    : null

  // Clamp dello step se l'utente ha cambiato tipo_operazione e ora STEPS è più corto
  const stepCorrente = STEPS[Math.min(stepIdx, STEPS.length - 1)]

  return (
    <div>
      <div className="page-header">
        <h1>{praticaId ? `Riprendi pratica${data.cliente_cognome ? ' · ' + data.cliente_cognome + ' ' + data.cliente_nome : ''}` : 'Nuova pratica'}</h1>
        <p>{praticaId ? 'Stai modificando una bozza esistente. I dati sono stati ricaricati.' : 'Compara polizze, prepara la pratica e invia al gestore.'}</p>
      </div>

      {praticaId && (
        <div className="info-box" style={{ marginBottom: '1rem' }}>
          📋 <strong>Bozza caricata.</strong> I tuoi dati sono già compilati negli step precedenti.
          Sei al passo <strong>{stepIdx + 1}/{STEPS.length} ({stepCorrente.label})</strong>.
          Puoi navigare con i pulsanti Indietro/Avanti per rivedere o modificare quello che hai inserito.
        </div>
      )}

      <div className="progress-bar">
        {STEPS.map((s, i) => (
          <div key={s.id} className={`progress-step ${i < stepIdx ? 'done' : i === stepIdx ? 'active' : ''}`} title={s.label} />
        ))}
      </div>

      <div className="card">
        <h2 className="card-title">{stepIdx + 1}. {stepCorrente.label}</h2>

        {stepCorrente.id === 'tipo' && (
          <StepTipoOperazione data={data} update={update} ruoloUtente={profile?.ruolo} />
        )}
        {stepCorrente.id === 'comuni' && (
          <StepDatiComuni
            data={data} update={update} updateComuni={updateComuni}
            province={tariffe.province}
            ruoloUtente={profile?.ruolo}
            tipoOperazione={data.tipo_operazione}
          />
        )}
        {stepCorrente.id === 'standalone' && (
          <StepStandalone data={data} update={update} prodotti={tariffe.standalone}
                          ruoloUtente={profile?.ruolo} />
        )}
        {stepCorrente.id === 'comparazione' && (
          <StepComparazione
            data={data} update={update}
            compagnie={tariffe.compagnie}
            standalone={tariffe.standalone}
            commissioneMax={COMMISSIONE_MAX_PCT}
            commissioneAccountPct={commissioneAccountPct}
            autocarriParametri={tariffe.parametriCompagnia[AUTOCARRI_INTERMEDIARI_CODICE] || {}}
            regione={regioneCliente}
          />
        )}
        {stepCorrente.id === 'documenti' && (
          <StepDocumenti
            data={data} update={update}
            praticaId={praticaId}
            persist={persist}
            documentiCompagnia={tariffe.documentiCompagnia || {}}
          />
        )}
        {stepCorrente.id === 'invio' && (
          <StepInvio
            data={data}
            update={update}
            praticaId={praticaId}
            persist={persist}
            tariffe={tariffe}
            onCompleted={() => navigate('/pratiche')}
          />
        )}

        <div className="btn-row">
          <button className="btn" onClick={prev} disabled={stepIdx === 0}>← Indietro</button>
          {stepIdx < STEPS.length - 1 && (
            <button className="btn btn-primary" onClick={async () => {
              // Persisti la pratica solo dopo aver scelto tipo + dati comuni minimi
              if (stepIdx >= 1) await persist()
              next()
            }} disabled={!canAdvance(stepCorrente.id, data) || saving}>
              {saving ? 'Salvataggio...' : 'Avanti →'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function canAdvance(stepId, d) {
  if (stepId === 'tipo') return !!d.tipo_operazione
  if (stepId === 'comuni') {
    if (!d.cliente_nome || !d.cliente_cognome) return false
    const dc = d.dati_comuni || {}
    // Identificativi veicolo OBBLIGATORI per TUTTI i flussi (incluso solo_standalone)
    if (!dc.targa || !String(dc.targa).trim()) return false
    if (!dc.marca_veicolo || !String(dc.marca_veicolo).trim()) return false
    if (!dc.modello_veicolo || !String(dc.modello_veicolo).trim()) return false
    // Flusso solo_standalone: bastano nome+cognome + targa/marca/modello
    if (d.tipo_operazione === 'solo_standalone') return true
    // Flusso polizza: anche provincia, valore, durata, causale, ecc.
    if (!dc.provincia || !dc.valore_auto || !dc.durata_mesi) return false
    if (!valoreVeicoloValido(dc.valore_auto, d.tipo_operazione).ok) return false
    if (isAutocarriIntermediari(d.tipo_operazione)) {
      if (dc.categoria_veicolo !== 'autocarro') return false
      if (!['fino_35', 'oltre_35'].includes(dc.peso_autocarro)) return false
      if (parseInt(dc.durata_mesi, 10) !== 12) return false
    }
    if (!dc.causale || (!isAutocarriIntermediari(d.tipo_operazione) && !dc.pacchetto)) return false
    if (!dc.immatricolazione) return false
    // Data immatricolazione non nel futuro
    if (new Date(dc.immatricolazione) > new Date()) return false
    // Causale "Nuova" coerente con immatricolazione (entro 30gg)
    if (!validaCausaleNuova(dc.causale, dc.immatricolazione).ok) return false
    // Causale "Rinnovo" coerente: serve veicolo già usato (>30gg dall'immatricolazione)
    if (!validaCausaleRinnovo(dc.causale, dc.immatricolazione).ok) return false
    // Se il cliente dichiara satellitare presente, marca + seriale obbligatori
    if (!isAutocarriIntermediari(d.tipo_operazione)) {
      if (dc.satellitare && (!dc.sat_marca || !dc.sat_seriale)) return false
      if (dc.acquista_lojack && ![12, 24, 36, 48, 60, 72].includes(parseInt(dc.mesi_lojack, 10))) return false
    }
    return true
  }
  if (stepId === 'standalone') {
    // Per solo_standalone serve almeno uno selezionato
    if (d.tipo_operazione === 'solo_standalone') {
      return Array.isArray(d.standalone_selezionati) && d.standalone_selezionati.length > 0
    }
    return true
  }
  if (stepId === 'comparazione') return !!d.scelta_compagnia
  if (stepId === 'documenti') {
    const dc = d.dati_comuni || {}
    // Data di decorrenza polizza: obbligatoria, da oggi a massimo +7 giorni
    const dd = dc.data_decorrenza ? new Date(dc.data_decorrenza) : null
    const oggiDd = new Date(); oggiDd.setHours(0, 0, 0, 0)
    const maxDd = new Date(oggiDd.getTime() + 7 * 24 * 60 * 60 * 1000)
    if (!dd || isNaN(dd.getTime())) return false
    dd.setHours(0, 0, 0, 0)
    if (dd < oggiDd || dd > maxDd) return false
    // Contatti contraente (obbligatori per tutti)
    if (!dc.cellulare_contraente || !/^[+\d][\d\s().\/-]{7,19}$/.test(dc.cellulare_contraente)) return false
    if (!dc.email_contraente || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dc.email_contraente)) return false
    // Codice fiscale obbligatorio per persone fisiche (per società basta P.IVA in cognome)
    if (!dc.is_societa) {
      if (!dc.codice_fiscale || !/^[A-Z0-9]{16}$/.test(dc.codice_fiscale)) return false
    }
    // Residenza contraente (sempre obbligatoria)
    if (!dc.indirizzo_residenza || !String(dc.indirizzo_residenza).trim()) return false
    if (!dc.cap_residenza || !/^\d{5}$/.test(dc.cap_residenza)) return false
    if (!dc.citta_residenza || !String(dc.citta_residenza).trim()) return false
    if (!dc.provincia_residenza || !/^[A-Z]{2}$/.test(dc.provincia_residenza)) return false
    // Per flusso polizza (NO solo_standalone) anche colore, km e numero telaio
    if (d.tipo_operazione !== 'solo_standalone') {
      if (!dc.colore_auto || !String(dc.colore_auto).trim()) return false
      if (dc.km == null || isNaN(parseInt(dc.km)) || parseInt(dc.km) < 0) return false
      if (!dc.numero_telaio || !/^[A-HJ-NPR-Z0-9]{17}$/.test(dc.numero_telaio)) return false
    }
    // Blocca se totale allegati supera 10 MB (body limit Edge Function Supabase)
    const allegati = d._allegati || {}
    const totalBytes = Object.values(allegati).reduce((sum, f) => sum + (f?.size || 0), 0)
    if (totalBytes > 10 * 1024 * 1024) return false
    return true
  }
  return true
}
